import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Component from '../../components/Component';
import {
  getCompetitionRailPayload,
  getGuestOrderPreview,
  getRankingLogic,
  isCompetitionRailApiSuccess,
  listCompetitionRail,
  syncCompetitionEntity,
  syncUpcomingLiveCompetitions,
  updateCompetitionRail,
} from '../../services/competitionRailApi';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

const STATUS_OPTIONS = ['live', 'upcoming', 'completed', 'scheduled'];
const CATALOG_PAGE_SIZE = 50;

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

function statusColor(status) {
  switch (status) {
    case 'live':
      return 'error';
    case 'upcoming':
      return 'warning';
    case 'completed':
      return 'default';
    case 'scheduled':
      return 'info';
    default:
      return 'default';
  }
}

function formatRankingLogic(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
}

function CompetitionRailTable({ items, onEdit, showPosition = false }) {
  if (!items?.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
        No competitions found.
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            {showPosition && <TableCell>Pos</TableCell>}
            <TableCell>Title</TableCell>
            <TableCell>CID</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Live</TableCell>
            <TableCell align="right">Upcoming</TableCell>
            <TableCell align="right">Admin score</TableCell>
            <TableCell align="right">Priority</TableCell>
            <TableCell>In rail</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow
              key={item.cid}
              hover
              sx={{
                bgcolor: item.status_mismatch ? 'warning.50' : undefined,
              }}
            >
              {showPosition && (
                <TableCell>
                  {item.home_rail_position ?? item.position ?? '-'}
                </TableCell>
              )}
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  {item.title || '-'}
                </Typography>
                {item.status_mismatch && (
                  <Tooltip
                    title={
                      item.suggested_status
                        ? `Suggested: ${item.suggested_status}`
                        : 'DB status disagrees with live match data'
                    }
                  >
                    <Chip label="Mismatch" size="small" color="warning" sx={{ mt: 0.5 }} />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>{item.cid}</TableCell>
              <TableCell>
                <Chip
                  label={item.status || '—'}
                  size="small"
                  color={statusColor(item.status)}
                  variant={item.has_live_match ? 'filled' : 'outlined'}
                />
              </TableCell>
              <TableCell align="right">{item.live_match_count ?? 0}</TableCell>
              <TableCell align="right">{item.upcoming_match_count ?? 0}</TableCell>
              <TableCell align="right">{item.admin_score ?? 0}</TableCell>
              <TableCell align="right">{item.priority_order ?? 0}</TableCell>
              <TableCell>
                {item.in_home_rail ? (
                  <Chip label="Yes" size="small" color="success" variant="outlined" />
                ) : (
                  <Chip label="No" size="small" variant="outlined" />
                )}
              </TableCell>
              <TableCell align="center">
                <IconButton size="small" onClick={() => onEdit(item)} aria-label="Edit">
                  <EditIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CompetitionRailDashboard() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [bulkSyncLoading, setBulkSyncLoading] = useState(false);
  const [error, setError] = useState('');

  const [homeRailData, setHomeRailData] = useState(null);
  const [catalogData, setCatalogData] = useState(null);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogSearchInput, setCatalogSearchInput] = useState('');
  const [catalogOffset, setCatalogOffset] = useState(0);

  const [rankingLogic, setRankingLogic] = useState(null);
  const [guestPreview, setGuestPreview] = useState(null);
  const [helpLoading, setHelpLoading] = useState(false);
  const [helpExpanded, setHelpExpanded] = useState(true);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formAdminScore, setFormAdminScore] = useState('');
  const [formPriorityOrder, setFormPriorityOrder] = useState('');
  const [formStatus, setFormStatus] = useState('upcoming');
  const [saving, setSaving] = useState(false);
  const [syncingEntity, setSyncingEntity] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleApiError = useCallback(
    (res, fallback) => {
      if (String(res?.statusCode || '') === '10003') {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 1500);
        return true;
      }
      if (!isCompetitionRailApiSuccess(res)) {
        setError(res?.message || fallback);
        return true;
      }
      return false;
    },
    [showSnackbar]
  );

  const fetchHomeRail = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listCompetitionRail({ scope: 'home_rail' });
      if (handleApiError(res, 'Failed to load home rail.')) {
        setHomeRailData(null);
        return;
      }
      setHomeRailData(getCompetitionRailPayload(res));
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load home rail.');
      setHomeRailData(null);
    } finally {
      setLoading(false);
    }
  }, [handleApiError]);

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listCompetitionRail({
        scope: 'all',
        search: catalogSearch || undefined,
        limit: CATALOG_PAGE_SIZE,
        offset: catalogOffset,
      });
      if (handleApiError(res, 'Failed to load catalog.')) {
        setCatalogData(null);
        return;
      }
      setCatalogData(getCompetitionRailPayload(res));
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load catalog.');
      setCatalogData(null);
    } finally {
      setLoading(false);
    }
  }, [catalogSearch, catalogOffset, handleApiError]);

  const fetchHelp = useCallback(async () => {
    setHelpLoading(true);
    try {
      const [logicRes, guestRes] = await Promise.all([
        getRankingLogic(),
        getGuestOrderPreview(),
      ]);
      if (isCompetitionRailApiSuccess(logicRes)) {
        setRankingLogic(getCompetitionRailPayload(logicRes) ?? logicRes?.data ?? logicRes);
      }
      if (isCompetitionRailApiSuccess(guestRes)) {
        setGuestPreview(getCompetitionRailPayload(guestRes));
      }
    } finally {
      setHelpLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 0) fetchHomeRail();
    if (tab === 1) fetchCatalog();
    if (tab === 2) fetchHelp();
  }, [tab, fetchHomeRail, fetchCatalog, fetchHelp]);

  const catalogPagination = catalogData?.pagination || {};
  const catalogTotal = catalogPagination.total ?? 0;
  const canCatalogPrev = catalogOffset > 0;
  const canCatalogNext = catalogOffset + CATALOG_PAGE_SIZE < catalogTotal;

  const homeRailItems = homeRailData?.items || [];
  const catalogItems = catalogData?.items || [];
  const embeddedRankingLogic = homeRailData?.ranking_logic || catalogData?.ranking_logic;

  const guestPreviewItems = useMemo(() => {
    if (Array.isArray(guestPreview?.items)) return guestPreview.items;
    if (Array.isArray(guestPreview)) return guestPreview;
    return [];
  }, [guestPreview]);

  const openEditDrawer = (item) => {
    setEditingItem(item);
    setFormAdminScore(String(item.admin_score ?? 0));
    setFormPriorityOrder(String(item.priority_order ?? 0));
    setFormStatus(item.status || 'upcoming');
    setDrawerOpen(true);
  };

  const closeEditDrawer = () => {
    setDrawerOpen(false);
    setEditingItem(null);
  };

  const refreshAfterMutation = async () => {
    await fetchHomeRail();
    if (tab === 1) await fetchCatalog();
    if (tab === 2) await fetchHelp();
  };

  const handleSave = async () => {
    if (!editingItem?.cid) return;
    setSaving(true);
    try {
      const body = {
        admin_score: Number(formAdminScore),
        priority_order: Number(formPriorityOrder),
        status: formStatus,
      };
      const res = await updateCompetitionRail(editingItem.cid, body);
      if (handleApiError(res, 'Failed to update competition.')) return;
      showSnackbar('Rail settings saved.');
      closeEditDrawer();
      await refreshAfterMutation();
    } finally {
      setSaving(false);
    }
  };

  const handleSyncEntity = async () => {
    if (!editingItem?.cid) return;
    setSyncingEntity(true);
    try {
      const res = await syncCompetitionEntity(editingItem.cid);
      if (handleApiError(res, 'Entity sync failed.')) return;
      const payload = getCompetitionRailPayload(res);
      const refreshed = payload?.item || payload;
      if (refreshed) {
        setEditingItem((prev) => ({ ...prev, ...refreshed }));
        setFormAdminScore(String(refreshed.admin_score ?? 0));
        setFormPriorityOrder(String(refreshed.priority_order ?? 0));
        setFormStatus(refreshed.status || 'upcoming');
      }
      showSnackbar('Synced from Entity Sports.');
      await refreshAfterMutation();
    } finally {
      setSyncingEntity(false);
    }
  };

  const handleMarkLive = async () => {
    setFormStatus('live');
    if (!editingItem?.cid) return;
    setSaving(true);
    try {
      const res = await updateCompetitionRail(editingItem.cid, { status: 'live' });
      if (handleApiError(res, 'Failed to mark live.')) return;
      showSnackbar('Marked as live.');
      setFormStatus('live');
      await refreshAfterMutation();
    } finally {
      setSaving(false);
    }
  };

  const handleBulkSync = async () => {
    setBulkSyncLoading(true);
    setError('');
    try {
      const res = await syncUpcomingLiveCompetitions();
      if (handleApiError(res, 'Bulk sync failed.')) return;
      const payload = getCompetitionRailPayload(res);
      const succeeded = payload?.succeeded ?? 0;
      const requested = payload?.requested ?? 0;
      const failed = payload?.failed?.length ?? 0;
      showSnackbar(
        `Bulk sync: ${succeeded}/${requested} succeeded${failed ? `, ${failed} failed` : ''}.`,
        failed ? 'warning' : 'success'
      );
      await refreshAfterMutation();
    } finally {
      setBulkSyncLoading(false);
    }
  };

  const handleCatalogSearch = (e) => {
    e.preventDefault();
    setCatalogSearch(catalogSearchInput.trim());
    setCatalogOffset(0);
  };

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <div className="d-flex align-items-center">
                  <IconButton style={{ background: '#FFFFFF' }} component={Link} to="/">
                    <ArrowBackIcon />
                  </IconButton>
                  <h3 style={{ marginLeft: '20px' }}>Home V2 Competition Rail</h3>
                </div>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 7, mt: 0.5 }}>
                  Manage the top 10 competitions on Home V2 (/home/v2/competition)
                </Typography>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                      <Tab label="Home rail (10)" />
                      <Tab label="Catalog" />
                      <Tab label="Help & preview" />
                    </Tabs>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        startIcon={bulkSyncLoading ? <CircularProgress size={16} /> : <SyncIcon />}
                        disabled={bulkSyncLoading || loading}
                        onClick={handleBulkSync}
                      >
                        Sync upcoming/live
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        disabled={loading}
                        onClick={() => {
                          if (tab === 0) fetchHomeRail();
                          else if (tab === 1) fetchCatalog();
                          else fetchHelp();
                        }}
                      >
                        Refresh
                      </Button>
                    </Box>
                  </Box>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                      {error}
                    </Alert>
                  )}

                  {embeddedRankingLogic && tab !== 2 && (
                    <Alert
                      severity="info"
                      sx={{ mb: 2, cursor: 'pointer' }}
                      onClick={() => setHelpExpanded((v) => !v)}
                    >
                      Ranking logic (tap to {helpExpanded ? 'collapse' : 'expand'})
                    </Alert>
                  )}
                  <Collapse in={helpExpanded && Boolean(embeddedRankingLogic) && tab !== 2}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, mb: 2, bgcolor: 'grey.50', maxHeight: 200, overflow: 'auto' }}
                    >
                      <Typography
                        component="pre"
                        variant="caption"
                        sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                      >
                        {formatRankingLogic(embeddedRankingLogic)}
                      </Typography>
                    </Paper>
                  </Collapse>

                  {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  )}

                  {!loading && (
                    <>
                      <TabPanel value={tab} index={0}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Pool limit: {homeRailData?.home_rail_limit ?? 10} — order shown in app
                          view-all rail.
                        </Typography>
                        <CompetitionRailTable
                          items={homeRailItems}
                          onEdit={openEditDrawer}
                          showPosition
                        />
                      </TabPanel>

                      <TabPanel value={tab} index={1}>
                        <Box
                          component="form"
                          onSubmit={handleCatalogSearch}
                          sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}
                        >
                          <TextField
                            size="small"
                            label="Search title, abbr, or CID"
                            value={catalogSearchInput}
                            onChange={(e) => setCatalogSearchInput(e.target.value)}
                            sx={{ minWidth: 280 }}
                          />
                          <Button type="submit" variant="contained">
                            Search
                          </Button>
                          {catalogSearch && (
                            <Button
                              variant="text"
                              onClick={() => {
                                setCatalogSearchInput('');
                                setCatalogSearch('');
                                setCatalogOffset(0);
                              }}
                            >
                              Clear
                            </Button>
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Raise priority_order to move a competition into the home rail top 10.
                        </Typography>
                        <CompetitionRailTable items={catalogItems} onEdit={openEditDrawer} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            {catalogTotal > 0
                              ? `Showing ${catalogOffset + 1}–${Math.min(
                                  catalogOffset + CATALOG_PAGE_SIZE,
                                  catalogTotal
                                )} of ${catalogTotal}`
                              : 'No results'}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              disabled={!canCatalogPrev}
                              onClick={() => setCatalogOffset((o) => Math.max(0, o - CATALOG_PAGE_SIZE))}
                            >
                              Previous
                            </Button>
                            <Button
                              size="small"
                              disabled={!canCatalogNext}
                              onClick={() => setCatalogOffset((o) => o + CATALOG_PAGE_SIZE)}
                            >
                              Next
                            </Button>
                          </Box>
                        </Box>
                      </TabPanel>

                      <TabPanel value={tab} index={2}>
                        {helpLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                          </Box>
                        ) : (
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                Ranking logic
                              </Typography>
                              <Paper
                                variant="outlined"
                                sx={{ p: 2, maxHeight: 360, overflow: 'auto', bgcolor: 'grey.50' }}
                              >
                                <Typography
                                  component="pre"
                                  variant="caption"
                                  sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                                >
                                  {formatRankingLogic(
                                    rankingLogic ?? embeddedRankingLogic ?? 'No data'
                                  )}
                                </Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                Guest order preview
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                How guests see the top-10 pool (admin_score, then priority_order).
                              </Typography>
                              <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>#</TableCell>
                                      <TableCell>Title</TableCell>
                                      <TableCell>CID</TableCell>
                                      <TableCell align="right">Guest score</TableCell>
                                      <TableCell>P1 preview</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {guestPreviewItems.length === 0 ? (
                                      <TableRow>
                                        <TableCell colSpan={5} align="center">
                                          No preview data
                                        </TableCell>
                                      </TableRow>
                                    ) : (
                                      guestPreviewItems.map((row, idx) => (
                                        <TableRow key={row.cid ?? idx}>
                                          <TableCell>{idx + 1}</TableCell>
                                          <TableCell>{row.title || '-'}</TableCell>
                                          <TableCell>{row.cid}</TableCell>
                                          <TableCell align="right">
                                            {row.guest_sort_score ?? row.admin_score ?? '-'}
                                          </TableCell>
                                          <TableCell>
                                            {row.is_p1_guest_preview ? (
                                              <Chip label="P1" size="small" color="primary" />
                                            ) : (
                                              '—'
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ))
                                    )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Grid>
                          </Grid>
                        )}
                      </TabPanel>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Drawer anchor="right" open={drawerOpen} onClose={closeEditDrawer}>
        <Box sx={{ width: { xs: '100vw', sm: 400 }, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h6">Edit rail settings</Typography>
              <Typography variant="body2" color="text.secondary">
                {editingItem?.title} (CID {editingItem?.cid})
              </Typography>
            </Box>
            <IconButton onClick={closeEditDrawer} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>

          {editingItem?.status_mismatch && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Status mismatch
              {editingItem.suggested_status
                ? ` — suggested: ${editingItem.suggested_status}`
                : ''}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Admin score"
              type="number"
              value={formAdminScore}
              onChange={(e) => setFormAdminScore(e.target.value)}
              fullWidth
              helperText="Ops boost for guest sort and personalization"
            />
            <TextField
              label="Priority order"
              type="number"
              value={formPriorityOrder}
              onChange={(e) => setFormPriorityOrder(e.target.value)}
              fullWidth
              helperText="Higher = more important; tie-breaker in rail"
            />
            <TextField
              select
              label="Status"
              value={formStatus}
              onChange={(e) => setFormStatus(e.target.value)}
              fullWidth
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            <Typography variant="caption" color="text.secondary">
              Live matches: {editingItem?.live_match_count ?? 0} · Upcoming:{' '}
              {editingItem?.upcoming_match_count ?? 0} · Completed:{' '}
              {editingItem?.completed_match_count ?? 0}
              {editingItem?.has_live_match != null && (
                <> · has_live_match: {String(editingItem.has_live_match)}</>
              )}
            </Typography>

            <Button
              variant="contained"
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              startIcon={syncingEntity ? <CircularProgress size={16} /> : <SyncIcon />}
              disabled={syncingEntity || saving}
              onClick={handleSyncEntity}
            >
              Sync from Entity Sports
            </Button>
            <Button variant="outlined" color="error" disabled={saving} onClick={handleMarkLive}>
              Mark live
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Component>
  );
}

export default CompetitionRailDashboard;
